import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import WarningIcon from '@mui/icons-material/Warning';

import { colors, typo } from '../../theme/tokens';

/**
 * WU12 — Drift item taxonomy.
 *   overwrite     — target already has a value that differs from what this
 *                   workflow would publish. Acknowledgment required.
 *   workflow-only — workflow has the entity, target doesn't (new add).
 *   target-only   — target has the entity, workflow doesn't (orphan).
 */
export type DriftType = 'overwrite' | 'workflow-only' | 'target-only';

export interface DriftItem {
  code: string;
  type: DriftType;
  oldValue?: string;
  newValue?: string;
}

export interface DriftWarningProps {
  driftItems: DriftItem[];
  /** Whether the user has acknowledged the overwrite items. */
  acknowledged: boolean;
  onAcknowledge: (next: boolean) => void;
}

const labelFor = (t: DriftType): string => {
  switch (t) {
    case 'overwrite':
      return 'Will overwrite target value';
    case 'workflow-only':
      return 'New (target does not have it)';
    case 'target-only':
      return 'Target-only (will be left untouched)';
  }
};

/**
 * Drift summary banner. Extracted from `PublishTab.tsx` (lines 629-657)
 * during WU12 so the promotion-workflow detail page and the legacy publish
 * tab can share the same affordance.
 *
 * Returns `null` when there are no items — callers can safely render this
 * unconditionally.
 */
const DriftWarning: React.FC<DriftWarningProps> = ({
  driftItems,
  acknowledged,
  onAcknowledge,
}) => {
  if (!driftItems || driftItems.length === 0) return null;

  const overwrites = driftItems.filter((d) => d.type === 'overwrite');
  const hasOverwrites = overwrites.length > 0;

  // Group items by type so the user can scan: "3 will overwrite, 2 new,
  // 1 orphan" is clearer than a flat list of seven rows.
  const grouped: Record<DriftType, DriftItem[]> = {
    overwrite: [],
    'workflow-only': [],
    'target-only': [],
  };
  for (const item of driftItems) grouped[item.type].push(item);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: '8px',
        backgroundColor: hasOverwrites ? colors.errorBg : colors.warningBg,
        border: `1px solid ${hasOverwrites ? colors.errorBorder : colors.warningBorder}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningIcon
          sx={{ color: hasOverwrites ? colors.errorIcon : colors.warningIcon, fontSize: 18 }}
        />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: hasOverwrites ? colors.errorText : colors.warningText,
          }}
        >
          {driftItems.length} drift {driftItems.length === 1 ? 'item' : 'items'} detected
        </Typography>
      </Box>

      {(Object.keys(grouped) as DriftType[]).map((type) => {
        const items = grouped[type];
        if (items.length === 0) return null;
        return (
          <Box key={type} sx={{ mb: 0.75 }}>
            <Typography
              sx={{ ...typo.tinyText, fontWeight: 700, textTransform: 'uppercase', mb: 0.25 }}
            >
              {labelFor(type)} ({items.length})
            </Typography>
            {items.map((item, i) => (
              <Box key={`${type}-${item.code}-${i}`} sx={{ pl: 2 }}>
                <Typography sx={{ ...typo.tinyText, color: colors.textSecondary }}>
                  <strong style={{ color: colors.textPrimary }}>{item.code}</strong>
                  {item.oldValue !== undefined && item.newValue !== undefined && (
                    <>
                      {' — '}
                      <span style={{ textDecoration: 'line-through' }}>{item.oldValue}</span>
                      {' → '}
                      <strong>{item.newValue}</strong>
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}

      {/*
        Acknowledgment is only required when at least one item is an
        overwrite. workflow-only and target-only items are informational —
        they don't require explicit consent because no existing value is
        being clobbered.
      */}
      {hasOverwrites && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => onAcknowledge(e.target.checked)}
              size="small"
              sx={{
                color: colors.errorIcon,
                '&.Mui-checked': { color: colors.errorIcon },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 12, color: colors.errorText, fontWeight: 600 }}>
              I understand this will overwrite {overwrites.length}{' '}
              {overwrites.length === 1 ? 'value' : 'values'} at the target environment.
            </Typography>
          }
        />
      )}
    </Paper>
  );
};

export default DriftWarning;
